import type { TenantRosterUser } from "@bezoned/shared/types/api/tenant/tenant-roster-get.types.js";
import { UserAvailabilityOptions } from "@bezoned/shared/types/user-presence.js";
import type { UserAvailability } from "@bezoned/shared/types/user-presence.js";
import type { FilterFn } from "@tanstack/react-table";

const TIME_ZONE_OFFSET_PATTERN = /^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/;
const WHITESPACE_PATTERN = /\s+/u;
const ROSTER_NAME_COLLATOR = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});

export const normalizeSearchValue = (value: string) =>
  value
    .normalize("NFKD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase();

export const getRosterName = (user: TenantRosterUser) =>
  user.displayName ?? user.email ?? user.msUserId;

function getPresenceSortGroup(availability: UserAvailability): number {
  if (availability === UserAvailabilityOptions.Available) {
    return 0;
  }

  if (availability === UserAvailabilityOptions.Offline) {
    return 2;
  }

  return 1;
}

function getLastNameSortValue(name: string): string {
  const trimmedName = name.trim();

  if (trimmedName === "") {
    return "";
  }

  const commaIndex = trimmedName.indexOf(",");

  if (commaIndex > 0) {
    return trimmedName.slice(0, commaIndex).trim();
  }

  const nameParts = trimmedName.split(WHITESPACE_PATTERN);
  return nameParts.at(-1) ?? trimmedName;
}

export function compareRosterUsersByPresenceAndLastName(
  leftUser: TenantRosterUser,
  rightUser: TenantRosterUser
): number {
  const presenceDiff =
    getPresenceSortGroup(leftUser.availability) -
    getPresenceSortGroup(rightUser.availability);

  if (presenceDiff !== 0) {
    return presenceDiff;
  }

  const leftName = getRosterName(leftUser);
  const rightName = getRosterName(rightUser);

  const lastNameDiff = ROSTER_NAME_COLLATOR.compare(
    getLastNameSortValue(leftName),
    getLastNameSortValue(rightName)
  );

  if (lastNameDiff !== 0) {
    return lastNameDiff;
  }

  const fullNameDiff = ROSTER_NAME_COLLATOR.compare(leftName, rightName);

  if (fullNameDiff !== 0) {
    return fullNameDiff;
  }

  return leftUser.msUserId.localeCompare(rightUser.msUserId);
}

export const rosterGlobalFilter: FilterFn<TenantRosterUser> = (
  row,
  _columnId,
  filterValue
) => {
  const searchValue = String(filterValue).trim();

  if (searchValue === "") {
    return true;
  }

  const normalizedSearchValue = normalizeSearchValue(searchValue);

  return [getRosterName(row.original), row.original.email]
    .filter((value): value is string => Boolean(value))
    .some((value) =>
      normalizeSearchValue(value).includes(normalizedSearchValue)
    );
};

export function getLocalTimeSortValue(timeZone: string | null, date: Date) {
  if (!timeZone) {
    return;
  }

  try {
    const hour = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      hourCycle: "h23",
      timeZone,
    })
      .formatToParts(date)
      .find((part) => part.type === "hour")?.value;
    const minute = new Intl.DateTimeFormat("en-US", {
      minute: "2-digit",
      timeZone,
    })
      .formatToParts(date)
      .find((part) => part.type === "minute")?.value;

    if (!(hour && minute)) {
      return;
    }

    return Number(hour) * 60 + Number(minute);
  } catch {
    // Some runtimes reject specific IANA names; callers handle missing offsets.
  }
}

export function getTimeZoneOffsetMinutes(
  timeZone: string,
  date: Date
): number | null {
  try {
    const timeZoneName = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
      timeZoneName: "shortOffset",
    })
      .formatToParts(date)
      .find((part) => part.type === "timeZoneName")?.value;

    if (!timeZoneName || timeZoneName === "GMT" || timeZoneName === "UTC") {
      return 0;
    }

    const match = timeZoneName.match(TIME_ZONE_OFFSET_PATTERN);

    if (!match) {
      return null;
    }

    const [, sign, hours, minutes = "00"] = match;
    const offsetMinutes = Number(hours) * 60 + Number(minutes);
    return sign === "+" ? offsetMinutes : -offsetMinutes;
  } catch {
    return null;
  }
}

export function formatTimeZoneDifference(deltaMinutes: number): string {
  return new Intl.NumberFormat("cs-CZ", {
    maximumFractionDigits: 2,
    signDisplay: "always",
  }).format(deltaMinutes / 60);
}
